import { AxiosRequestConfig, AxiosResponse } from "axios";
import { gatewaySectorWorkFlows } from "../../../gateways/gatewaySectorWorkFlows";

export interface IQueryParams {
  [key: string]: string | number | boolean | undefined | null;
}

export interface IGetActionParams {
  uri: string;
  queryParams?: IQueryParams;
  config?: AxiosRequestConfig;
}

export interface IPostActionParams<TBody> {
  uri: string;
  body?: TBody;
  queryParams?: IQueryParams;
  config?: AxiosRequestConfig;
}

export interface IWorkflowQueryKeyParams {
  scope: string;
  uri: string;
  queryParams?: IQueryParams;
}

export interface IDeleteActionParams {
  uri: string;
  queryParams?: IQueryParams;
  config?: AxiosRequestConfig;
}

interface IDeleteActionWithBodyParams<TBody> {
  uri: string;
  body?: TBody;
  config?: AxiosRequestConfig;
}

export interface IPatchActionParams<TBody> {
  uri: string;
  body?: TBody;
  queryParams?: IQueryParams;
  config?: AxiosRequestConfig;
}

const sanitizeQueryParams = (queryParams?: IQueryParams): Record<string, string | number | boolean> | undefined => {
  if (!queryParams) {
    return undefined;
  }

  const filteredEntries = Object.entries(queryParams).filter(([, value]) => {
    return value !== undefined && value !== null;
  });

  if (!filteredEntries.length) {
    return undefined;
  }

  return filteredEntries.reduce<Record<string, string | number | boolean>>((accumulator, [key, value]) => {
    if (value !== undefined && value !== null) {
      accumulator[key] = value;
    }

    return accumulator;
  }, {});
};

export const getAction = async <TResponse>(params: IGetActionParams): Promise<TResponse> => {
  const { uri, queryParams, config } = params;

  const response: AxiosResponse<TResponse> = await gatewaySectorWorkFlows.get(uri, {
    ...config,
    params: sanitizeQueryParams(queryParams),
  });

  return response.data;
};

export const workflowQueryKeys = {
  all: ["sector-workflows"] as const,

  catalogRecords: (uri: string, queryParams?: IQueryParams) => [...workflowQueryKeys.all, "catalog-records", uri, queryParams ?? {}] as const,
};

export const postAction = async <TResponse, TBody>(params: IPostActionParams<TBody>): Promise<TResponse> => {
  const { uri, body, queryParams, config } = params;

  const response: AxiosResponse<TResponse> = await gatewaySectorWorkFlows.post(uri, body, {
    ...config,
    params: sanitizeQueryParams(queryParams),
  });

  return response.data;
};

export const patchSectorWorkflowAction = async <TResponse, TBody>(params: IPatchActionParams<TBody>): Promise<TResponse> => {
  const { uri, body, queryParams, config } = params;

  const response: AxiosResponse<TResponse> = await gatewaySectorWorkFlows.patch(uri, body, {
    ...config,
    params: sanitizeQueryParams(queryParams),
  });

  return response.data;
};

// single delete
export const deleteWorkflowItemAction = async <TResponse>(params: IDeleteActionParams): Promise<TResponse> => {
  const { uri, queryParams, config } = params;

  const response: AxiosResponse<TResponse> = await gatewaySectorWorkFlows.delete(uri, {
    ...config,
    params: sanitizeQueryParams(queryParams),
  });

  return response.data;
};

// bulk delete
export const deleteActionWithBody = async <TResponse, TBody = unknown>(params: IDeleteActionWithBodyParams<TBody>): Promise<TResponse> => {
  const { uri, body, config } = params;

  const response: AxiosResponse<TResponse> = await gatewaySectorWorkFlows.delete(uri, {
    ...config,
    data: body,
  });

  return response.data;
};
